import { useMemo, useState, useEffect } from 'react';
import { getAllRejectionCodes } from '@/api/rejectioncode.api';

interface BarItem {
  label: string;
  value: number;
  color: string;
}

function SimpleBarChart({ data, title, emptyText }: { data: BarItem[]; title: string; emptyText: string }) {
  const maxVal = Math.max(...data.map((d) => d.value), 1);
  const hasData = data.length > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
      {hasData ? (
        <div className="flex items-end gap-2 h-32 mt-2">
          {data.map((item) => (
            <div key={item.label} className="flex-1 flex flex-col items-center gap-1">
              <span className="text-xs text-gray-600 font-medium">{item.value}</span>
              <div className="w-full flex justify-center">
                <div
                  className="w-full max-w-[48px] rounded-t"
                  style={{ height: `${Math.max((item.value / maxVal) * 100, 8)}%`, minHeight: '4px', backgroundColor: item.color }}
                />
              </div>
              <span className="text-xs text-gray-500 truncate w-full text-center">{item.label}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-400 text-sm">
          <i className="ri-bar-chart-line text-2xl mb-1 block" />
          {emptyText}
        </div>
      )}
    </div>
  );
}

function categorizeDefect(description: string): string {
  const d = description.toLowerCase();
  if (d.includes('scratch') || d.includes('dent') || d.includes('surface') || d.includes('visual') || d.includes('finish')) return 'Surface / Visual';
  if (d.includes('dimension') || d.includes('length') || d.includes('width') || d.includes('tolerance') || d.includes('size')) return 'Dimensional Error';
  if (d.includes('pack') || d.includes('label') || d.includes('mark')) return 'Packaging Defect';
  if (d.includes('weight') || d.includes('mass') || d.includes('density')) return 'Weight Issue';
  if (d.includes('material') || d.includes('composition') || d.includes('grade')) return 'Material Issue';
  return 'Other';
}

export default function NCRReportsTab({ ncrs: propNcrs }: { ncrs?: any[] }) {
  const [rejectionCodes, setRejectionCodes] = useState<any[]>([]);
  const ncrsList = propNcrs || [];

  useEffect(() => {
    let active = true;
    async function loadData() {
      try {
        const res = await getAllRejectionCodes();
        if (active && res.success && res.data) {
          setRejectionCodes(res.data);
        }
      } catch (err) {
        console.error('Failed to load rejection codes in NCRReportsTab:', err);
      }
    }
    loadData();
    return () => {
      active = false;
    };
  }, []);

  const charts = useMemo(() => {
    // Chart 1: NCRs by Month
    const last3Months = ['Feb 2024', 'Mar 2024', 'Apr 2024'];
    const months: BarItem[] = last3Months.map((label) => {
      const [monthStr, yearStr] = label.split(' ');
      const monthIdx = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
      const yearNum = parseInt(yearStr, 10);
      const count = ncrsList.filter((n) => {
        const cd = new Date(n.createdAt);
        return cd.getMonth() === monthIdx && cd.getFullYear() === yearNum;
      }).length;
      return { label, value: count, color: '#4f46e5' };
    });

    // Chart 2: NCRs by Defect Category
    const defectMap = new Map<string, number>();
    ncrsList.forEach((n) => {
      const cat = categorizeDefect(n.defectDescription || '');
      defectMap.set(cat, (defectMap.get(cat) || 0) + 1);
    });
    const defectColors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6'];
    const defects: BarItem[] = Array.from(defectMap.entries()).map(([label, value], i) => ({
      label,
      value,
      color: defectColors[i % defectColors.length],
    }));

    // Chart 3: NCRs by Stage
    const stageMap = new Map<string, number>();
    ncrsList.forEach((n) => {
      if (n.type) stageMap.set(n.type, (stageMap.get(n.type) || 0) + 1);
    });
    const stageColors: Record<string, string> = {
      INCOMING: '#4f46e5',
      IN_PROCESS: '#f97316',
      FINAL: '#22c55e',
    };
    const stages: BarItem[] = Array.from(stageMap.entries()).map(([label, value]) => ({
      label: label.replace('_', '-'),
      value,
      color: stageColors[label] || '#94a3b8',
    }));

    // Chart 4: Disposition Summary
    const dispMap = new Map<string, number>();
    ncrsList.forEach((n) => {
      if (n.dispositionAction) dispMap.set(n.dispositionAction, (dispMap.get(n.dispositionAction) || 0) + 1);
    });
    const dispColors: Record<string, string> = {
      REJECT: '#ef4444',
      REWORK: '#f97316',
      USE_AS_IS: '#22c55e',
      RETURN_TO_SUPPLIER: '#3b82f6',
      PENDING: '#94a3b8',
    };
    const dispositions: BarItem[] = Array.from(dispMap.entries()).map(([label, value]) => ({
      label: label.replace(/_/g, ' '),
      value,
      color: dispColors[label] || '#94a3b8',
    }));

    // Chart 5: Rejection Code Distribution (from live rejection codes)
    const byCategory = rejectionCodes.reduce<Record<string, number>>((acc, r) => {
      const key = r.category || 'OTHER';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const rejectionColors = ['#ef4444', '#f97316', '#eab308', '#3b82f6', '#8b5cf6', '#22c55e'];
    const rejections: BarItem[] = Object.entries(byCategory).map(([label, value], i) => ({
      label,
      value,
      color: rejectionColors[i % rejectionColors.length],
    }));

    return { months, defects, stages, dispositions, rejections };
  }, [rejectionCodes]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <SimpleBarChart data={charts.months} title="NCRs by Month" emptyText="No NCR data for recent months" />
      <SimpleBarChart data={charts.defects} title="NCRs by Defect Category" emptyText="No defect data available" />
      <SimpleBarChart data={charts.stages} title="NCRs by Stage" emptyText="No stage data available" />
      <SimpleBarChart data={charts.dispositions} title="Disposition Summary" emptyText="No disposition data available" />
      <SimpleBarChart data={charts.rejections} title="Rejection Code Distribution" emptyText="No rejection data available" />
    </div>
  );
}